// Cálculo de maturidade (por pergunta e geral) — extraído de
// src/pages/AnswerQuestionnaire/index.tsx (tela de fechamento/Relatório de
// Maturidade) pra ser reaproveitado também pelo Relatório de Auditoria
// (src/pages/RelatorioAuditoria), garantindo que os dois mostrem exatamente os
// mesmos números em vez de duas fórmulas que podem divergir com o tempo.

export type ChecklistItemResposta = 'SIM' | 'NAO' | 'NA';

export interface ChecklistItemPeso {
  id: number;
  ativo?: boolean;
  percentual_peso?: number;
}

export interface ChecklistRequisitoPeso {
  itens: ChecklistItemPeso[];
}

export interface PerguntaPeso {
  qp_id: number;
  peso: number;
}

// Quando a pergunta tem checklist, sua pontuação (0-100) é: o Peso configurado
// para a pergunta (ex.: 40) + o restante até 100 (ex.: 60) distribuído entre os
// itens do checklist NÃO marcados como N/A. Itens N/A saem tanto do numerador
// quanto do denominador — não reduzem a pontuação, só redistribuem o peso entre
// os itens restantes. Se os itens têm percentual_peso configurado no Cadastro de
// Perguntas, a divisão respeita esse peso; se nenhum item tem peso configurado
// (soma = 0), divide em partes iguais entre os itens respondidos.
// Retorna null só quando a pergunta não tem checklist nenhum (nesse caso quem chama
// usa a resposta SIM/NÃO da própria pergunta). Se a pergunta TEM checklist mas
// nenhum item foi respondido ainda, retorna o Peso da Pergunta (sem crédito do
// checklist) — pendente não pode contar como se já tivesse 100%.
export function getChecklistPct(
  pergunta: PerguntaPeso,
  requisitos: ChecklistRequisitoPeso[] | undefined,
  respostasItem: Record<number, ChecklistItemResposta>
): number | null {
  if (!requisitos || requisitos.length === 0) return null;

  const itensValidos: { resposta: ChecklistItemResposta; peso: number }[] = [];
  let algumRespondido = false;

  requisitos.forEach(req => {
    req.itens.forEach(item => {
      if (item.ativo === false) return;
      const resposta = respostasItem[item.id];
      if (!resposta) return;
      algumRespondido = true;
      if (resposta === 'NA') return;
      // Number(...): percentual_peso vem como string do backend (coluna DECIMAL) — sem
      // converter, a soma abaixo virava concatenação de texto ("5.00"+"10.00"), não soma.
      itensValidos.push({ resposta, peso: Number(item.percentual_peso) || 0 });
    });
  });

  const pesoPergunta = pergunta.peso ?? 100;

  // Checklist existe mas nenhum item foi respondido ainda: conta só o Peso da
  // Pergunta (sem crédito do checklist) — não é o mesmo caso de "não tem checklist".
  if (!algumRespondido) return pesoPergunta;

  const somaConfigurada = itensValidos.reduce((s, i) => s + i.peso, 0);
  const usaPesoIgual = somaConfigurada <= 0;
  let totalPeso = 0;
  let pesoConquistado = 0;
  itensValidos.forEach(i => {
    const peso = usaPesoIgual ? 1 : i.peso;
    totalPeso += peso;
    if (i.resposta === 'SIM') pesoConquistado += peso;
  });

  const poolChecklist = Math.max(0, 100 - pesoPergunta);
  const checklistPct = totalPeso > 0 ? (pesoConquistado / totalPeso) * poolChecklist : 0;
  return pesoPergunta + checklistPct;
}

// Maturidade geral: respostas N/A não entram na contagem de conformidade (nem no
// numerador, nem no denominador — mesmo critério do getChecklistPct acima).
export function calcMaturity(
  respondidas: PerguntaPeso[],
  answers: Record<number, any>,
  checklistData: Record<number, ChecklistRequisitoPeso[]>,
  checklistAnswers: Record<number, Record<number, ChecklistItemResposta>>
): number {
  if (respondidas.length === 0) return 0;
  let totalWeight = 0;
  let score = 0;
  respondidas.forEach(p => {
    const v = answers[p.qp_id];
    if (v === 'NA') return;
    const w = p.peso || 100;
    totalWeight += w;
    const checklistPct = getChecklistPct(p, checklistData[p.qp_id], checklistAnswers[p.qp_id] || {});
    if (checklistPct !== null) score += (checklistPct / 100) * w;
    else if (v === 'SIM') score += w;
    else if (v === 'NAO') score += 0;
    else if (typeof v === 'number') score += (v / 100) * w;
    else if (v && v.toString().trim()) score += w * 0.5;
  });
  return totalWeight > 0 ? (score / totalWeight) * 100 : 0;
}
